import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Col,
  Row,
  UncontrolledTooltip,
  Card,
  CardBody,
  Badge,
  Button,
} from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import Spinners from "Components/Common/Spinner";
import { getStatusColor, readableDate } from "utils";
import DeleteModal from "../../Components/Common/DeleteModal";
import WeighBillViewModal from "./WeighBillViewModal";
import { useDeleteWeighBill, useGetWeighBills } from "hooks/useWeighBill";
import { AppRoutes } from "../../constants";
import { capitalize } from "lodash";
import { UpdateWeighbillStatus } from "./components";

function WeighBill() {
  document.title = "Papermap | Weigh Bills";

  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [selectedBill, setSelectedBill] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false); // State for edit modal
  const toggleEditModal = (data) => {
    setSelectedBill(data);
    setShowEditModal(!showEditModal);
  };

  // Add your API hooks here
  const { data: weighBills, isLoading } = useGetWeighBills();
  const { mutate: deleteWeighBill } = useDeleteWeighBill();

  const handleBillClick = () => {
    navigate(AppRoutes.CREATE_WEIGHBILL);
  };

  // View functionality
  const toggleViewModal = useCallback(
    (bill: any) => {
      setSelectedBill(bill);
      setViewModal(!viewModal);
    },
    [viewModal]
  );

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "bill_id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => (
          <Link to="#" className="text-body fw-bold">
            {cellProps.row.original.id}
          </Link>
        ),
      },
      {
        header: "Tracking no.",
        accessorKey: "tracking_number",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Supplier",
        accessorKey: "supplier_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => (
          <div>{cellProps.row.original.supplier.name}</div>
        ),
      },

      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) =>
          readableDate(cellProps.row.original.created_at),
      },
      {
        header: "Delivery date",
        accessorKey: "expected_delivery_date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) =>
          readableDate(cellProps.row.original.created_at),
      },
      {
        header: "Status",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps: any) => {
          const status = cellProps.row.original.status;
          return (
            <Badge
              className={`font-size-12 badge-soft-${getStatusColor(status)}`}
            >
              {capitalize(cellProps.row.original?.status)}
            </Badge>
          );
        },
      },
      {
        header: "Update Status",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => (
          <Button
            type="button"
            color="soft-info"
            className="btn-sm btn-rounded "
            onClick={() => toggleEditModal(cellProps.cell.row.original)}
          >
            Update
          </Button>
        ),
      },

      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li id="viewtooltip">
              <Link
                to={`/view-weigh-bill/${cellProps.row.original.id}`}
                className="btn btn-sm btn-soft-primary"
                // onClick={() => toggleViewModal(cellProps.row.original)}
              >
                <i className="mdi mdi-eye-outline" />
                <UncontrolledTooltip placement="top" target="viewtooltip">
                  View
                </UncontrolledTooltip>
              </Link>
            </li>
            <li id="edittooltip">
              <Link
                to={`/edit-weigh-bill/${cellProps.row.original.id}`}
                className="btn btn-sm btn-soft-info"
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip placement="top" target="edittooltip">
                  Edit
                </UncontrolledTooltip>
              </Link>
            </li>
            <li id="deleteTooltip">
              <Link
                to="#"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(cellProps.row.original)}
              >
                <i className="mdi mdi-delete" />
                <UncontrolledTooltip placement="top" target="deleteTooltip">
                  Delete
                </UncontrolledTooltip>
              </Link>
            </li>
          </ul>
        ),
      },
    ],
    [handleBillClick, toggleViewModal]
  );

  // Delete functionality
  const onClickDelete = (bill: any) => {
    setSelectedBill(bill);
    setDeleteModal(true);
  };

  const handleDeleteBill = () => {
    if (selectedBill?.id) {
      deleteWeighBill(selectedBill.id);
      setDeleteModal(false);
    }
  };

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteBill}
        onCloseClick={() => setDeleteModal(false)}
      />

      <WeighBillViewModal
        isOpen={viewModal}
        toggle={() => setViewModal(!viewModal)}
        weighBill={selectedBill}
      />

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Inventory" breadcrumbItem="Weigh Bills" />
          {isLoading ? (
            <Spinners />
          ) : (
            <Row>
              <Col xs={12}>
                <Card>
                  <CardBody>
                    <TableContainer
                      columns={columns}
                      data={weighBills || []}
                      isGlobalFilter={true}
                      isAddButton={true}
                      isCustomPageSize={true}
                      handleUserClick={handleBillClick}
                      isPagination={true}
                      SearchPlaceholder="Search..."
                      buttonClass="btn btn-success btn-rounded waves-effect waves-light mb-2 me-2 addOrder-modal"
                      buttonName="Add New Weigh Bill"
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                      theadClass="table-light"
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </div>
      <UpdateWeighbillStatus
        isOpen={showEditModal}
        toggle={toggleEditModal}
        editDetails={selectedBill}
      />
    </React.Fragment>
  );
}

export default WeighBill;
